import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  Box,
  TextField,
  IconButton,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Typography from 'Components/UI/Typography';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useTranscriptSegmentFinder from './useTranscriptSegmentFinder';
import TranscriptSummaryPicker from './TranscriptSummaryPicker';
import { useTranscriptContext } from './TranscriptContext';

const TRANSCRIPT_HEADER_HEIGHT = 200;

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    position: 'relative',
  },
  search: {
    margin: theme.spacing(2, 6, 0, 6),
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchInput: {
    backgroundColor: theme.palette.common.white,
    width: '100%',
    borderRadius: theme.radius.sm,
  },
  searchButtons: {
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    justifyContent: 'center',
  },
  list: {
    margin: theme.spacing(1, 3),
    padding: theme.spacing(2, 4),
    borderRadius: theme.radius.sm,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    flexGrow: 1,
    '& mark': {
      backgroundColor: theme.palette.secondary.light,
    },
    '& mark[data-selected="true"]': {
      backgroundColor: theme.palette.facet.accentGreen4,
    },
  },
  listItem: {
    padding: theme.spacing(1.5),
  },
  speaker: {
    fontSize: 18,
    fontWeight: theme.typography.fontWeightBold,
    paddingBottom: theme.spacing(1),
  },
  timestamp: {
    fontSize: 16,
    paddingBottom: theme.spacing(1),
  },
  placeholder: {
    fontSize: 14,
    alignSelf: 'center',
    padding: theme.spacing(1, 0),
  },
  content: {
    fontSize: 16,
    whiteSpace: 'pre-wrap',
    fontWeight: theme.typography.fontWeightMedium,
  },
  mark: {
    scrollMarginTop: `${TRANSCRIPT_HEADER_HEIGHT}px`,
  },
  title: {
    marginTop: theme.spacing(1),
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.common.white,
    paddingBottom: theme.spacing(2),
  },
});

const matchKey = id => `transcript-segement-id-${id}`; // used to format and set IDs of selected content

function TranscriptSegmentList({ classes }) {
  const {
    selectedResource: resource,
    setAttributeOnSelectedResource: setAttribute,
    refreshCollection,
  } = useTranscriptContext();

  const {
    state: { currentFilter, selected, matches },
    setFilter,
    next,
    previous,
  } = useTranscriptSegmentFinder(matchKey);

  const segments = resource?.attr('segments').resources().list() || [];

  const handleSummarySubmit = React.useCallback(async (value) => {
    try {
      setAttribute('summaryOverride', value);
      await resource.submit();
      refreshCollection();
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }, [resource, setAttribute]);

  const regex = new RegExp(`(${currentFilter})`, 'ig');
  let matchEncounter = 0; // We record the matches(count of currentFilter in segments[n].content) so we know how to scroll to them later
  return (
    <div className={classes.root}>
      <TranscriptSummaryPicker
        meetingTranscriptResource={resource}
        content={resource?.attr('summary')}
        handleSubmit={handleSummarySubmit}
      />

      <div className={classes.stickyHeader}>
        <Typography className={classes.title} color="primary" variant="h3">
          Transcript
        </Typography>
        {segments.length > 0 && (
        <div className={classes.search}>
          <TextField
            placeholder="Search Transcript"
            variant="outlined"
            className={classes.searchInput}
            inputProps={{ min: 0, style: { textAlign: 'center' } }}
            color="primary"
            onChange={e => setFilter(e.target.value)}
            onKeyPress={(ev) => {
              if (ev.key === 'Enter') {
                ev.preventDefault();
                next();
              }
            }}
          />
          {currentFilter && (
          <div className={classes.searchButtons}>
            <IconButton
              disabled={!matches || !currentFilter}
              color="primary"
              onClick={previous}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography>
              {matches !== 0 ? `${selected + 1} of ${matches}` : '0 of 0'}
            </Typography>
            <IconButton
              disabled={!matches || !currentFilter}
              color="primary"
              onClick={next}
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
          )}
        </div>
        )}
      </div>
      {segments.length > 0
        ? segments.map(segment => {
          return (
            <Box className={classes.listItem} key={segment.id()}>
              <Typography className={classes.speaker}>
                {segment.attr('speakerName')}
              </Typography>
              {/* NOTE: Remove this field until Gong Transcript start timestamps are obtained. */}
              {/* <Typography className={classes.timestamp}> */}
              {/*  {segment.attr('startTime')} */}
              {/* </Typography> */}
              <Typography className={classes.content}>
                {(() => {
                  if (currentFilter) {
                    const contentFragments = segment
                      .attr('content')
                      .split(regex);
                    return contentFragments.map((contentFragment) => {
                      const id = matchKey(matchEncounter);
                      const currentEncounter = Number(matchEncounter);
                      const hasEncounter = regex.test(contentFragment);
                      if (hasEncounter) {
                        matchEncounter += 1;
                      }
                      return hasEncounter ? (
                        <mark
                          id={id}
                          data-selected={currentEncounter === selected}
                          className={classes.mark}
                        >
                          {contentFragment}
                        </mark>
                      ) : contentFragment;
                    });
                  }
                  return segment.attr('content').trim();
                })()}
              </Typography>
            </Box>
          );
        })
        : (
          <Typography className={classes.placeholder} color="darkGray">
            <i>{'No transcript available, check back later'}</i>
          </Typography>
        )}
    </div>
  );
}

TranscriptSegmentList.propTypes = {
  classes: PropTypes.object.isRequired,
};

TranscriptSegmentList.defaultProps = {};

export default withStyles(styles)(TranscriptSegmentList);
